import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { errorMessage } from '@/components/Toast';
import { Button, Field, Input, Spinner } from '@/components/ui';
import { AuthAside } from './AuthAside';

type VerifyState = 'missing' | 'verifying' | 'verified' | 'failed';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'missing');
  const [error, setError] = useState<string | null>(null);

  // Resend form (shown when there is no token or verification failed).
  const [email, setEmail] = useState('');
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resent, setResent] = useState(false);

  // The token is single-use on the server, so guard against the effect firing
  // twice (React StrictMode re-runs mount effects in development).
  const attempted = useRef(false);
  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api.auth
      .verifyEmail(token)
      .then(() => setState('verified'))
      .catch((err) => {
        setError(errorMessage(err));
        setState('failed');
      });
  }, [token]);

  async function onResend(e: FormEvent) {
    e.preventDefault();
    setResendBusy(true);
    setResendError(null);
    try {
      await api.auth.resendVerification(email.trim());
      setResent(true);
    } catch (err) {
      setResendError(errorMessage(err));
    } finally {
      setResendBusy(false);
    }
  }

  const resendForm = resent ? (
    // Deliberately identical whether or not the address exists — no enumeration.
    <p>If an account exists for that address, a new verification email is on its way.</p>
  ) : (
    <form className="stack gap-4" onSubmit={onResend}>
      <Field label="Email" hint="We'll send you a fresh verification link." error={resendError ?? undefined}>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@firm.com" required />
      </Field>
      <Button type="submit" className="block" variant="secondary" loading={resendBusy}>Resend verification email</Button>
    </form>
  );

  return (
    <div className="auth">
      <AuthAside />
      <div className="auth-main">
        <div className="auth-card">
          <p className="eyebrow">Email verification</p>
          <h1>Verify your email</h1>

          {state === 'verifying' && <Spinner label="Verifying your email…" />}

          {state === 'verified' && (
            <p>
              Email verified — you&rsquo;re all set.{' '}
              <Link to="/login" className="inline-link">Sign in</Link> to continue.
            </p>
          )}

          {state === 'missing' && (
            <div className="stack gap-4">
              <p>This verification link is invalid. Enter your email to request a new one.</p>
              {resendForm}
            </div>
          )}

          {state === 'failed' && (
            <div className="stack gap-4">
              <p className="field"><span className="err">{error}</span></p>
              {resendForm}
            </div>
          )}

          {state !== 'verified' && (
            <p className="muted mt-5 t-sm">
              Already verified? <Link to="/login" className="inline-link">Sign in</Link>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
